/* ============================================
   SocialMedia — Main page for Social Media module
   3-column layout with AI chat, content area, and
   tab navigation via URL params.
   ============================================ */
import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSupabase } from '@/hooks/useSupabase'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { showToast } from '@/components/layout/Toast'
import type { SocialPost, SocialStrategy } from '@/lib/types'
import ContentCalendar from '@/components/social-media/ContentCalendar'
import PostComposer from '@/components/social-media/PostComposer'
import AIChatPanel from '@/components/social-media/AIChatPanel'
import StrategyPanel from '@/components/social-media/StrategyPanel'
import AnalyticsDashboard from '@/components/social-media/AnalyticsDashboard'
import VisualReviewModal from '@/components/social-media/VisualReviewModal'
import { PageHeader } from '@/components/ui'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

type SocialTab = 'calendar' | 'nuevo' | 'estrategia' | 'analytics'

const TAB_CONFIG: { key: SocialTab; label: string; icon: React.ReactNode }[] = [
  {
    key: 'calendar',
    label: 'Calendario',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    ),
  },
  {
    key: 'nuevo',
    label: 'Nuevo Post',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
      </svg>
    ),
  },
  {
    key: 'estrategia',
    label: 'Estrategia',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
      </svg>
    ),
  },
  {
    key: 'analytics',
    label: 'Analytics',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
      </svg>
    ),
  },
]

function resolveTab(param: string | undefined): SocialTab {
  if (param === 'nuevo' || param === 'estrategia' || param === 'analytics') return param
  return 'calendar'
}

export default function SocialMedia() {
  const { tab } = useParams<{ tab?: string }>()
  const navigate = useNavigate()
  const db = useSupabase()
  const { session } = useAuth()

  const activeTab = resolveTab(tab)

  // Data state
  const [posts, setPosts] = useState<SocialPost[]>([])
  const [strategy, setStrategy] = useState<SocialStrategy | null>(null)
  const [marcoAvatar, setMarcoAvatar] = useState('/assets/brand/marco-avatar.svg')

  // UI state
  const [showAIChat, setShowAIChat] = useState(false)
  const [editPost, setEditPost] = useState<SocialPost | null>(null)
  const [initialDate, setInitialDate] = useState<Date | null>(null)
  const [isGeneratingImages, setIsGeneratingImages] = useState(false)
  const [showVisualReview, setShowVisualReview] = useState(false)
  // Track how many posts were sent to generate (persisted to detect generation in progress)
  const generationBatchRef = useRef<number[]>([])

  // Load posts
  const loadPosts = useCallback(async () => {

    try {
      const data = await db.getAll<SocialPost>('social_posts')
      // Sort by publication_date DESC
      data.sort((a, b) => {
        const da = a.publication_date ? new Date(a.publication_date).getTime() : 0
        const db_ = b.publication_date ? new Date(b.publication_date).getTime() : 0
        return db_ - da
      })
      setPosts(data)

      // Auto-detect if generation is in progress:
      // If there are posts in draft with image_prompt but no media AND
      // some posts already moved to pending_review, generation is running
      const draftsNeedingImage = data.filter(p => p.status === 'draft' && p.image_prompt && (!p.media_urls || p.media_urls.length === 0))
      const pendingReview = data.filter(p => p.status === 'pending_review')
      if (draftsNeedingImage.length > 0 && pendingReview.length > 0) {
        setIsGeneratingImages(true)
      } else if (draftsNeedingImage.length === 0 && isGeneratingImages) {
        // All done
        setIsGeneratingImages(false)
        if (generationBatchRef.current.length > 0) {
          showToast(`${pendingReview.length} imágenes generadas — listas para revisión`, 'success')
          generationBatchRef.current = []
        }
      }
    } catch (err) {
      console.error('Error loading posts:', err)
      showToast('Error al cargar publicaciones', 'error')
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load strategy (singleton)
  const loadStrategy = useCallback(async () => {
    try {
      const data = await db.getAll<SocialStrategy>('social_strategy')
      setStrategy(data.length > 0 ? data[0] : null)
    } catch (err) {
      console.error('Error loading strategy:', err)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadPosts()
    loadStrategy()
    // Load Marco avatar
    supabase.from('agente_config').select('avatar_sm_url,avatar_url').eq('slug', 'marco').single().then(({ data }: any) => {
      if (data?.avatar_sm_url) setMarcoAvatar(data.avatar_sm_url)
      else if (data?.avatar_url) setMarcoAvatar(data.avatar_url)
    }).catch(() => {})
  }, [loadPosts, loadStrategy])

  // NOTE: Supabase Realtime channel was removed (was dead code via compat layer stub).
  // TODO: Add SSE realtime for social posts when backend emits social_post_updated events.
  // Use: useRealtimeEvent('social_post_updated', (data) => { ... })

  // Tab navigation
  const switchTab = (newTab: SocialTab) => {
    if (newTab === 'calendar') {
      navigate('/social')
    } else {
      navigate(`/social/${newTab}`)
    }
    // Clear editing state when switching
    setEditPost(null)
    setInitialDate(null)
  }

  // Calendar handlers
  const handleSelectPost = (post: SocialPost) => {
    setEditPost(post)
    setInitialDate(null)
    navigate('/social/nuevo')
  }

  const handleSelectDate = (date: Date) => {
    setEditPost(null)
    setInitialDate(date)
    navigate('/social/nuevo')
  }

  // Composer handlers
  const handleSavePost = async (postData: Partial<SocialPost>) => {
    try {
      if (postData.id) {
        await db.update('social_posts', postData as Record<string, unknown>)
        showToast('Publicación actualizada', 'success')
      } else {
        await db.add('social_posts', postData as Record<string, unknown>)
        showToast('Borrador guardado', 'success')
      }
      await loadPosts()
      navigate('/social')
    } catch (err) {
      console.error('Error saving post:', err)
      showToast('Error al guardar publicación', 'error')
    }
  }

  const handleSchedulePost = async (postData: Partial<SocialPost>) => {
    try {
      if (postData.id) {
        await db.update('social_posts', postData as Record<string, unknown>)
        showToast('Publicación programada', 'success')
      } else {
        await db.add('social_posts', postData as Record<string, unknown>)
        showToast('Publicación programada', 'success')
      }
      await loadPosts()
      navigate('/social')
    } catch (err) {
      console.error('Error scheduling post:', err)
      showToast('Error al programar publicación', 'error')
    }
  }

  // Stats for header
  const totalPosts = posts.length
  const publishedPosts = posts.filter(p => p.status === 'published').length
  const scheduledPosts = posts.filter(p => p.status === 'scheduled').length
  const proposalPosts = posts.filter(p => p.status === 'proposal').length

  // Proposal handlers
  const handleApproveProposals = async () => {
    try {
      const proposals = posts.filter(p => p.status === 'proposal')
      for (const p of proposals) {
        await db.update('social_posts', { id: p.id, status: 'draft' } as Record<string, unknown>)
      }
      showToast(`${proposals.length} propuestas aprobadas como borradores`, 'success')
      await loadPosts()
    } catch (err) {
      console.error('Error approving proposals:', err)
      showToast('Error al aprobar propuestas', 'error')
    }
  }

  const handleRejectProposals = async () => {
    try {
      const proposals = posts.filter(p => p.status === 'proposal')
      for (const p of proposals) {
        await db.remove('social_posts', p.id)
      }
      showToast(`${proposals.length} propuestas eliminadas`, 'success')
      await loadPosts()
    } catch (err) {
      console.error('Error rejecting proposals:', err)
      showToast('Error al rechazar propuestas', 'error')
    }
  }

  // ── Pipeline handlers ──

  // Step 2: Generate images — fire-and-forget (server processes independently)
  const handleGenerateImages = () => {
    const drafts = posts.filter(p => p.status === 'draft' && p.image_prompt && (!p.media_urls || p.media_urls.length === 0))
    if (drafts.length === 0) return

    // Track the batch for progress
    generationBatchRef.current = drafts.map(p => p.id)
    setIsGeneratingImages(true)
    showToast(`Generando ${drafts.length} imágenes con Gemini AI... Puedes navegar libremente`, 'info')

    // Fire and forget — the edge function runs server-side
    // Realtime subscription will update the UI as each image completes
    fetch(
      `${API_URL}/api/social/image-ai`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'generate-for-posts',
          postIds: drafts.map(p => p.id),
        }),
      }
    ).catch(err => {
      console.error('Error triggering image generation:', err)
      showToast('Error al iniciar generación de imágenes', 'error')
      setIsGeneratingImages(false)
      generationBatchRef.current = []
    })
  }

  // Step 3: Visual approval handlers
  const handleApproveVisual = async (postId: number) => {
    try {
      await db.update('social_posts', { id: postId, status: 'approved' } as Record<string, unknown>)
      showToast('Imagen aprobada', 'success')
      await loadPosts()
    } catch (err) {
      console.error('Error approving visual:', err)
      showToast('Error al aprobar imagen', 'error')
    }
  }

  const handleApproveAllVisual = async () => {
    try {
      const pending = posts.filter(p => p.status === 'pending_review')
      for (const p of pending) {
        await db.update('social_posts', { id: p.id, status: 'approved' } as Record<string, unknown>)
      }
      showToast(`${pending.length} imágenes aprobadas`, 'success')
      setShowVisualReview(false)
      await loadPosts()
    } catch (err) {
      console.error('Error approving all visuals:', err)
      showToast('Error al aprobar imágenes', 'error')
    }
  }

  const handleRejectVisual = async (postId: number) => {
    try {
      await db.update('social_posts', { id: postId, status: 'draft', media_urls: [] } as Record<string, unknown>)
      showToast('Imagen rechazada — vuelve a generar', 'success')
      await loadPosts()
    } catch (err) {
      console.error('Error rejecting visual:', err)
      showToast('Error al rechazar imagen', 'error')
    }
  }

  const handleRegenerateImage = async (postId: number, newPrompt?: string) => {
    try {
      // Optionally update the prompt first
      if (newPrompt) {
        await db.update('social_posts', { id: postId, image_prompt: newPrompt } as Record<string, unknown>)
      }
      // Clear current image and set back to draft
      await db.update('social_posts', { id: postId, status: 'draft', media_urls: [] } as Record<string, unknown>)
      showToast('Regenerando imagen...', 'info')
      await loadPosts()

      // Trigger generation for this single post
      const res = await fetch(
        `${API_URL}/api/social/image-ai`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'generate-for-posts',
            postIds: [postId],
          }),
        }
      )
      const data = await res.json()
      if (data.ok) {
        showToast('Imagen regenerada', 'success')
      } else {
        showToast(data.error || 'Error al regenerar', 'error')
      }
      await loadPosts()
    } catch (err) {
      console.error('Error regenerating image:', err)
      showToast('Error al regenerar imagen', 'error')
    }
  }

  // Step 4: Publish to Metricool
  const handlePublishToMetricool = async () => {
    const approved = posts.filter(p => p.status === 'approved')
    if (approved.length === 0) return

    try {
      const res = await fetch(
        `${API_URL}/api/social/publish-metricool`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ postIds: approved.map(p => p.id) }),
        }
      )
      const data = await res.json()
      if (data.ok) {
        const successCount = data.results.filter((r: { status: string }) => r.status === 'scheduled').length
        showToast(`${successCount} posts enviados a Metricool`, 'success')
      } else {
        showToast(data.error || 'Error al publicar en Metricool', 'error')
      }
      await loadPosts()
    } catch (err) {
      console.error('Error publishing to Metricool:', err)
      showToast('Error al publicar en Metricool', 'error')
    }
  }

  return (
    <div className="page-enter sm-page">
      {/* Page header */}
      <PageHeader
        breadcrumbs={[{ label: 'Marketing' }, { label: 'Social Media' }]}
        meta={`${totalPosts} posts`}
        actions={
          <div className="sm-page__quick-stats">
            <span className="sm-page__quick-stat sm-page__quick-stat--published">{publishedPosts} publicados</span>
            <span className="sm-page__quick-stat sm-page__quick-stat--scheduled">{scheduledPosts} programados</span>
            {proposalPosts > 0 && (
              <span className="sm-page__quick-stat sm-page__quick-stat--proposal">{proposalPosts} propuestas</span>
            )}
          </div>
        }
      />

      {/* Tab bar */}
      <div className="sm-tabs">
        {TAB_CONFIG.map(t => (
          <button
            key={t.key}
            type="button"
            className={`sm-tabs__tab ${activeTab === t.key ? 'sm-tabs__tab--active' : ''}`}
            onClick={() => switchTab(t.key)}
          >
            {t.icon}
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* Main layout */}
      <div className="sm-layout">

        {/* Main content area */}
        <div className="sm-layout__content">
          {activeTab === 'calendar' && (
            <ContentCalendar
              posts={posts}
              onSelectPost={handleSelectPost}
              onSelectDate={handleSelectDate}
              onApproveProposals={handleApproveProposals}
              onRejectProposals={handleRejectProposals}
              onGenerateImages={handleGenerateImages}
              onOpenVisualReview={() => setShowVisualReview(true)}
              onPublishToMetricool={handlePublishToMetricool}
              isGeneratingImages={isGeneratingImages}
            />
          )}
          {activeTab === 'nuevo' && (
            <PostComposer
              editPost={editPost}
              initialDate={initialDate}
              strategy={strategy}
              onSave={handleSavePost}
              onSchedule={handleSchedulePost}
            />
          )}
          {activeTab === 'estrategia' && (
            <StrategyPanel
              strategy={strategy}
              onSave={async (data) => {
                try {
                  if (strategy?.id) {
                    await db.update('social_strategy', { id: strategy.id, ...data } as Record<string, unknown>)
                    showToast('Estrategia actualizada', 'success')
                  } else {
                    await db.add('social_strategy', data as Record<string, unknown>)
                    showToast('Estrategia creada', 'success')
                  }
                  await loadStrategy()
                } catch (err) {
                  console.error('Error saving strategy:', err)
                  showToast('Error al guardar estrategia', 'error')
                }
              }}
            />
          )}
          {activeTab === 'analytics' && (
            <AnalyticsDashboard posts={posts} />
          )}
        </div>
      </div>

      {/* Marco Chatbot — Floating */}
      <AIChatPanel
        isOpen={showAIChat}
        onToggle={() => setShowAIChat(!showAIChat)}
        strategy={strategy}
        onPostGenerated={(draft) => {
          setEditPost(null)
          setInitialDate(null)
          navigate('/social/nuevo')
          handleSavePost({ ...draft, status: 'draft' })
        }}
        onStrategyUpdated={loadStrategy}
        onCalendarProposalCreated={async () => {
          await loadPosts()
          navigate('/social')
        }}
      />
      {!showAIChat && (
        <button
          type="button"
          className="marco-chat-bubble"
          onClick={() => setShowAIChat(true)}
          title="Hablar con Marco"
        >
          <img
            src={marcoAvatar}
            alt="Marco"
            className="marco-chat-bubble__img"
          />
        </button>
      )}

      {/* Visual Review Modal */}
      {showVisualReview && (
        <VisualReviewModal
          posts={posts.filter(p => p.status === 'pending_review')}
          onClose={() => setShowVisualReview(false)}
          onApprove={handleApproveVisual}
          onApproveAll={handleApproveAllVisual}
          onReject={handleRejectVisual}
          onRegenerate={handleRegenerateImage}
        />
      )}
    </div>
  )
}
